/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */
import React, { Component } from 'react';
import PropTypes from "prop-types";
import { EuiPopover } from './popover';
import { EuiPortal } from '../portal';
/**
 * Injects the EuiPopover next to the button via EuiPortal
 * then the button element is moved into the popover dom.
 * On unmount, the button is moved back to its original location.
 */

export class EuiWrappingPopover extends Component {
  portal = null;
  anchor = null;

  componentDidMount() {
    if (this.anchor) {
      this.anchor.insertAdjacentElement('beforebegin', this.props.button);
    }
  }

  componentWillUnmount() {
    if (this.props.button.parentNode) {
      if (this.portal) {
        this.portal.insertAdjacentElement('beforebegin', this.props.button);
      }
    }
  }

  setPortalRef = node => {
    this.portal = node;
  };
  setAnchorRef = node => {
    this.anchor = node;
  };

  render() {
    const {
      button,
      ...rest
    } = this.props;
    return <EuiPortal portalRef={this.setPortalRef} insert={{
      sibling: this.props.button,
      position: 'after'
    }}>
        <EuiPopover {...rest} button={<div ref={this.setAnchorRef} className="euiWrappingPopover__anchor" />} />
      </EuiPortal>;
  }

}
EuiWrappingPopover.propTypes = {
  button: PropTypes.any.isRequired
};

try {
  EuiWrappingPopover.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/popover/wrapping_popover.tsx',
    description: 'Injects the EuiPopover next to the button via EuiPortal\n' + 'then the button element is moved into the popover dom.\n' + 'On unmount, the button is moved back to its original location.',
    displayName: 'EuiWrappingPopover',
    methods: [],
    props: {
      button: {
        defaultValue: null,
        description: 'Triggering element for which to align the popover to',
        name: 'button',
        parent: {
          fileName: 'app/src/components/popover/wrapping_popover.tsx',
          name: 'EuiWrappingPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/wrapping_popover.tsx',
          name: 'EuiWrappingPopoverProps'
        }],
        required: true,
        type: {
          name: 'HTMLElement'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + 'Provide a name to the popover panel\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }, {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      anchorClassName: {
        defaultValue: null,
        description: 'Class name passed to the direct parent of the button',
        name: 'anchorClassName',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      anchorPosition: {
        defaultValue: null,
        description: 'Alignment of the popover and arrow relative to the button',
        name: 'anchorPosition',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"downLeft" | "upCenter" | "upLeft" | "upRight" | "downCenter" | "downRight" | "leftCenter" | "leftUp" | "leftDown" | "rightCenter" | "rightUp" | "rightDown"',
          value: [{
            value: '"downLeft"'
          }, {
            value: '"upCenter"'
          }, {
            value: '"upLeft"'
          }, {
            value: '"upRight"'
          }, {
            value: '"downCenter"'
          }, {
            value: '"downRight"'
          }, {
            value: '"leftCenter"'
          }, {
            value: '"leftUp"'
          }, {
            value: '"leftDown"'
          }, {
            value: '"rightCenter"'
          }, {
            value: '"rightUp"'
          }, {
            value: '"rightDown"'
          }]
        }
      },
      attachToAnchor: {
        defaultValue: null,
        description: 'Style and position alteration for arrow-less, left-aligned\n' + 'attachment. Intended for use with inputs as anchors, e.g.\n' + 'EuiInputPopover',
        name: 'attachToAnchor',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      buttonRef: {
        defaultValue: null,
        description: '',
        name: 'buttonRef',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLDivElement) => void'
        }
      },
      closePopover: {
        defaultValue: null,
        description: 'Callback to handle hiding of the popover',
        name: 'closePopover',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: true,
        type: {
          name: 'NoArgCallback<void>'
        }
      },
      container: {
        defaultValue: null,
        description: "Restrict the popover's position within this element",
        name: 'container',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'HTMLElement'
        }
      },
      display: {
        defaultValue: null,
        description: 'CSS display type for both the popover and anchor',
        name: 'display',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"inlineBlock" | "block"',
          value: [{
            value: '"inlineBlock"'
          }, {
            value: '"block"'
          }]
        }
      },
      focusTrapProps: {
        defaultValue: null,
        description: 'Object of props passed to EuiFocusTrap',
        name: 'focusTrapProps',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Pick<EuiFocusTrapProps, "shards" | "scrollLock" | "noIsolation" | "clickOutsideDisables">'
        }
      },
      hasArrow: {
        defaultValue: null,
        description: 'Show arrow indicating to originating button',
        name: 'hasArrow',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      initialFocus: {
        defaultValue: null,
        description: 'Specifies what element should initially have focus; Can be a DOM\n' + 'node, or a selector string (which will be passed to\n' + 'document.querySelector() to find the DOM node), or a function that\n' + 'returns a DOM node\n' + 'Set to `false` to prevent initial auto-focus. Use only\n' + 'when your app handles setting initial focus state.',
        name: 'initialFocus',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'false | ElementTarget'
        }
      },
      insert: {
        defaultValue: null,
        description: 'Passed directly to EuiPortal for DOM positioning. Both properties are\n' + 'required if prop is specified',
        name: 'insert',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '{ sibling: HTMLElement; position: "after" | "before"; }'
        }
      },
      isOpen: {
        defaultValue: null,
        description: 'Visibility state of the popover',
        name: 'isOpen',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      ownFocus: {
        defaultValue: null,
        description: 'Traps tab focus within the popover contents',
        name: 'ownFocus',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      panelClassName: {
        defaultValue: null,
        description: 'Custom class added to the EuiPanel containing the popover contents',
        name: 'panelClassName',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      panelPaddingSize: {
        defaultValue: null,
        description: 'EuiPanel padding on all sides',
        name: 'panelPaddingSize',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"xs" | "s" | "m" | "l" | "xl" | "none"',
          value: [{
            value: '"xs"'
          }, {
            value: '"s"'
          }, {
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }, {
            value: '"none"'
          }]
        }
      },
      panelStyle: {
        defaultValue: null,
        description: 'Standard DOM `style` attribute. Passed to the EuiPanel',
        name: 'panelStyle',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'CSSProperties'
        }
      },
      panelProps: {
        defaultValue: null,
        description: 'Object of props passed to EuiPanel',
        name: 'panelProps',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Omit<(DisambiguateSet<_EuiPanelButtonlike, _EuiPanelDivlike> & _EuiPanelDivlike) | (DisambiguateSet<_EuiPanelDivlike, _EuiPanelButtonlike> & _EuiPanelButtonlike), "style">'
        }
      },
      panelRef: {
        defaultValue: null,
        description: '',
        name: 'panelRef',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '(instance: HTMLElement) => void'
        }
      },
      popoverRef: {
        defaultValue: null,
        description: '',
        name: 'popoverRef',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'Ref<HTMLDivElement>'
        }
      },
      repositionOnScroll: {
        defaultValue: null,
        description: "When `true`, the popover's position is re-calculated when the user\n" + 'scrolls, this supports having fixed-position popover anchors. When nesting\n' + 'an `EuiPopover` in a scrollable container, `repositionOnScroll` should be `true`',
        name: 'repositionOnScroll',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      zIndex: {
        defaultValue: null,
        description: 'By default, popover content inherits the z-index of the anchor\n' + 'component; pass `zIndex` to override',
        name: 'zIndex',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      onTrapDeactivation: {
        defaultValue: null,
        description: 'Function callback for when the focus trap is deactivated',
        name: 'onTrapDeactivation',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: '() => void'
        }
      },
      offset: {
        defaultValue: null,
        description: 'Distance away from the anchor that the popover will render',
        name: 'offset',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number'
        }
      },
      buffer: {
        defaultValue: null,
        description: 'Minimum distance between the popover and the bounding container;\n' + 'Pass an array of 4 values to adjust each side differently: `[top, right, bottom, left]`\n' + 'Default is 16',
        name: 'buffer',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'number | [number, number, number, number]'
        }
      },
      arrowChildren: {
        defaultValue: null,
        description: 'Element to pass as the child element of the arrow;\n' + 'Use case is typically limited to an accompanying `EuiBeacon`',
        name: 'arrowChildren',
        parent: {
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        },
        declarations: [{
          fileName: 'app/src/components/popover/popover.tsx',
          name: 'EuiPopoverProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      }
    },
    extendedInterfaces: ['EuiWrappingPopoverProps', 'CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'EuiPopoverProps']
  };
} catch (e) {}